<template>
  <v-dialog
    v-model="value"
    persistent
    max-width="800px"
    :scrollable="errorsList !== null"
  >
    <v-card :loading="loading" :disabled="loading">
      <v-card-title>
        <v-row justify="center" class="ma-4" v-if="!errorResponse">
          <span class="text-h5" v-if="errorsList === null">{{ $t('colleges.massive_load') }}</span>
          <span class="text-h5" v-else>
            {{ uploadStatus === 'COMPLETED' && !errorsList.length
              ? $t('colleges.successful_upload')
              : `${$t('colleges.error_list')} [${evaluateErrors()}]` }}
          </span>
        </v-row>
        <v-row justify="center" class="ma-4" v-else>
          <span class="text-h5">{{ $t('common.support_text') }}</span>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-container v-if="!errorResponse">
          <v-row v-if="errorsList === null">
            <v-col cols="12" class="py-3" v-if="school.id">
              <v-text-field
                :label="$t('colleges.school_id')"
                disabled
                readonly
                outlined
                hide-details
                v-model="school.id"
              />
            </v-col>
            <v-col cols="12" class="py-3" v-if="school.id">
              <v-select
                :label="$t('colleges.payment_identifier')"
                outlined
                hide-details
                v-model="paymentIdentifier"
                :items="formatPaymentByTypeIdentifierOptions"
                :disabled="!!school.payment_by_type_id"
              />
            </v-col>
            <v-col cols="12" class="py-3">
              <v-select
                :label="$t('colleges.excel_format')"
                outlined
                hide-details
                :items="formatOptions"
                v-model="excelFormat"
                :disabled="!school.id"
              />
                <a :href="formatOptionsExamples[excelFormat]" target="_blank">
                  {{ $t('colleges.see_an_example') }}
                </a>
            </v-col>
            <!-- Do not erase, ask Chain or Ronny for context. 03-03-2022.-->
            <!-- <v-col cols="12" class="py-3">
              <v-select
                :label="$t('colleges.users_have_DNI_label')"
                outlined
                hide-details
                v-model="usersHaveDNI"
                :items="usersHaveDNIOptions"
              />
            </v-col> -->
            <v-col cols="12" class="py-3">
              <v-select
                :label="$t('colleges.dissable_old_data_label')"
                outlined
                hide-details
                v-model="disableOldData"
                :items="disableOldDataOptions"
              />
              <span class="red--text" v-if="disableOldData">
                {{ $t('colleges.massive_load_warning') }}
              </span>
            </v-col>
            <v-col cols="12" class="py-3">
              <v-file-input
                :label="$t('colleges.file')"
                :placeholder="$t('colleges.file_placeholder')"
                outlined
                hide-details
                prepend-icon
                prepend-inner-icon="$file"
                show-size
                accept=".xls, .xlsx"
                v-model="file"
              />
            </v-col>
            <v-col cols="12" class="py-3" v-if="loading && jobId">
              <v-progress-linear
                :value="progressPercent"
                height="12"
                color="deep-purple lighten-2"
                striped
              >
                <strong>{{ progressPercent }}%</strong>
              </v-progress-linear>
              <div class="text-caption text-center mt-2" v-if="progressStatusLabel">
                {{ progressStatusLabel }}
              </div>
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col cols="12">
              <p v-for="(err, index) in errorsList" :key="index" v-html="(index + 1) + ': ' + err ">
              </p>
              <p v-if="!errorsList.length" class="text-center">
                {{ successMessage || $t('colleges.successful_upload') }}
              </p>
            </v-col>

            <v-col cols="12" v-if="schoolSummaries.length > 0" class="mt-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-2">
                {{ $t('colleges.upload_summary') }}
              </h3>
              <v-expansion-panels accordion flat>
                <v-expansion-panel
                  v-for="sede in schoolSummaries"
                  :key="sede.schoolId"
                >
                  <v-expansion-panel-header>
                    <span>
                      <strong>{{ sede.schoolName }}</strong>
                      <span class="ml-2 text-caption">
                        ({{ $t('colleges.upload_summary_expected') }}: {{ sede.totalUsers || 0 }}
                        | {{ $t('colleges.upload_summary_processed') }}: {{ sede.totalCount || 0 }}
                        <span v-if="(sede.totalUsers || 0) - (sede.totalCount || 0) > 0" class="red--text">
                          | {{ $t('colleges.upload_summary_not_processed') }}: {{ (sede.totalUsers || 0) - (sede.totalCount || 0) }}
                        </span>)
                      </span>
                    </span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="mb-2 text-body-2">
                      {{ $t('colleges.upload_summary_directives') }}: {{ sede.directivesCount }}
                      &nbsp;|&nbsp;
                      {{ $t('colleges.upload_summary_teachers') }}: {{ sede.teachersCount }}
                      &nbsp;|&nbsp;
                      {{ $t('colleges.upload_summary_students') }}: {{ sede.studentsCount }}
                    </div>
                    <v-simple-table dense v-if="sede.courses && sede.courses.length > 0">
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th>{{ $t('colleges.upload_summary_aula') }}</th>
                            <th class="text-center">{{ $t('colleges.upload_summary_expected') }}</th>
                            <th class="text-center">{{ $t('colleges.upload_summary_processed') }}</th>
                            <th class="text-center">{{ $t('colleges.upload_summary_not_processed') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="course in sede.courses" :key="course.courseId">
                            <td>{{ course.courseName }}</td>
                            <td class="text-center">{{ course.totalUsers || 0 }}</td>
                            <td class="text-center">{{ course.totalCount || 0 }}</td>
                            <td
                              class="text-center"
                              :class="{ 'red--text': (course.totalUsers || 0) - (course.totalCount || 0) > 0 }"
                            >
                              {{ (course.totalUsers || 0) - (course.totalCount || 0) }}
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </v-container>
        <v-container v-else>
          <v-row>
            <v-col cols="12" class="py-3">
              <p class="text-center">
                {{ $t('common.support_desc') }}
              </p>
              <p class="text-center">
                {{ errorResponse }}
              </p>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-row
          justify="space-around"
          class="my-6"
          v-if="errorsList === null && errorResponse === false"
        >
          <v-btn
            rounded
            text
            color="deep-purple lighten-2"
            @click.prevent="$emit('onCancel')"
          >
            {{$t('common.cancel')}}

          </v-btn>
          <v-btn
            rounded
            outlined
            color="deep-purple lighten-2"
            :disabled="file === null"
            @click.prevent="sendForm"
          >
            {{$t('common.send')}}
          </v-btn>
        </v-row>
        <v-row justify="space-around" class="my-6" v-else>
          <v-btn
            rounded
            text
            color="deep-purple lighten-2"
            @click.prevent="close"
          >
            {{$t('common.close')}}

          </v-btn>
          <v-btn
            rounded
            outlined
            color="deep-purple lighten-2"
            @click.prevent="saveTxt"
            v-if="evaluateErrors() !== 0 || schoolSummaries.length > 0"
          >
            {{$t('colleges.save_as_txt')}}
          </v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { saveAs } from 'file-saver';
import {
  formatInput,
  formatOptions,
  formatOptionsExamples,
  formatPaymentByTypeIdentifierOptions,
} from '@/constants/data/FormatInputMassiveLoad.json';
import massiveUserLoadService from '@/services/massiveUserLoad.service';

export default {
  name: 'MassiveLoadModal',
  props: {
    school: { type: Object, default: () => ({ id: 0, payment_by_type_id: null }) },
    value: { type: Boolean, default: false },
  },
  data: () => ({
    formatInput,
    formatOptions,
    formatOptionsExamples,
    formatPaymentByTypeIdentifierOptions,
    paymentIdentifier: {},
    excelFormat: 'BeeReaders',
    file: null,
    usersHaveDNI: true,
    disableOldData: false,
    loading: false,
    jobId: null,
    progressPercent: 0,
    uploadStatus: null,
    pollingTimer: null,
    errorsList: null,
    successMessage: null,
    errorResponse: false,
    schoolSummaries: [],
  }),
  computed: {
    usersHaveDNIOptions() {
      return [
        { value: true, text: this.$t('colleges.users_have_DNI_yes') },
        { value: false, text: this.$t('colleges.users_have_DNI_no') },
      ];
    },
    disableOldDataOptions() {
      return [
        { value: true, text: this.$t('colleges.dissable_old_data_yes') },
        { value: false, text: this.$t('colleges.dissable_old_data_no') },
      ];
    },
    progressStatusLabel() {
      switch (this.uploadStatus) {
      case 'PENDING':
        return this.$t('colleges.massive_load_pending');
      case 'PROCESSING':
        return this.$t('colleges.massive_load_processing');
      case 'FAILED':
        return this.$t('colleges.massive_load_failed');
      case 'COMPLETED':
        return this.$t('colleges.massive_load_completed');
      default:
        return '';
      }
    },
  },
  methods: {
    resetProgressState() {
      this.jobId = null;
      this.progressPercent = 0;
      this.uploadStatus = null;
      this.schoolSummaries = [];
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },
    startPolling() {
      this.stopPolling();
      if (!this.jobId) {
        return;
      }
      this.pollingTimer = setInterval(this.fetchProgress, 30000);
      this.fetchProgress();
    },
    async fetchProgress() {
      try {
        const response = await massiveUserLoadService.getProgress(this.jobId);
        const data = response.data.content;
        this.uploadStatus = data.status;
        this.progressPercent = data.progressPercent || 0;

        if (data.status === 'COMPLETED' || data.status === 'FAILED') {
          this.stopPolling();
          this.loading = false;
          this.schoolSummaries = data.schoolSummaries || [];
          this.errorsList = (data.errors && data.errors.length) ? data.errors : [];
          this.successMessage = data.message || null;
        }
      } catch (error) {
        this.stopPolling();
        this.loading = false;
        this.errorResponse = error;
      }
    },
    close() {
      this.stopPolling();
      this.resetProgressState();
      this.errorResponse = false;
      this.file = null;
      this.errorsList = null;
      this.successMessage = null;
      this.loading = false;
      this.$emit('onCancel');
    },
    evaluateErrors() {
      return this.errorsList?.length || 0;
    },
    findPaymentIdentifier(currentPaymentIdentifier) {
      if (!currentPaymentIdentifier) return null;
      return this.formatPaymentByTypeIdentifierOptions.find(
        (item) => item.value === currentPaymentIdentifier,
      );
    },
    saveTxt() {
      let content = (this.errorsList || []).map((err, i) => `${i + 1}: ${err}`).join('\n');

      if (this.schoolSummaries.length > 0) {
        content += `\n\n--- ${this.$t('colleges.upload_summary')} ---\n`;
        this.schoolSummaries.forEach((sede) => {
          const notProcessed = (sede.totalUsers || 0) - (sede.totalCount || 0);
          content += `\n${this.$t('colleges.upload_summary_sede')}: ${sede.schoolName || ''}`;
          content += `\n  ${this.$t('colleges.upload_summary_expected')}: ${sede.totalUsers || 0}`;
          content += `\n  ${this.$t('colleges.upload_summary_processed')}: ${sede.totalCount || 0}`;
          if (notProcessed > 0) {
            content += `\n  ${this.$t('colleges.upload_summary_not_processed')}: ${notProcessed}`;
          }
          content += `\n  ${this.$t('colleges.upload_summary_directives')}: ${sede.directivesCount || 0}`;
          content += `\n  ${this.$t('colleges.upload_summary_teachers')}: ${sede.teachersCount || 0}`;
          content += `\n  ${this.$t('colleges.upload_summary_students')}: ${sede.studentsCount || 0}`;

          if (sede.courses && sede.courses.length > 0) {
            sede.courses.forEach((course) => {
              const courseNotProcessed = (course.totalUsers || 0) - (course.totalCount || 0);
              content += `\n    ${this.$t('colleges.upload_summary_aula')}: ${course.courseName || ''}`;
              content += ` | ${this.$t('colleges.upload_summary_expected')}: ${course.totalUsers || 0}`;
              content += ` | ${this.$t('colleges.upload_summary_processed')}: ${course.totalCount || 0}`;
              if (courseNotProcessed > 0) {
                content += ` | ${this.$t('colleges.upload_summary_not_processed')}: ${courseNotProcessed}`;
              }
            });
          }
          content += '\n';
        });
      }
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, `errors_${this.errorsList.length}.txt`);
    },
    async sendForm() {
      this.loading = true;
      this.errorResponse = false;
      this.errorsList = null;
      this.resetProgressState();

      try {
        const response = await massiveUserLoadService.startMassiveUpload(
          this.school.id,
          this.formatInput[this.excelFormat],
          true,
          this.file,
          this.usersHaveDNI,
          this.disableOldData,
        );

        if (response?.data?.content) {
          this.jobId = response.data.content.jobId;
          this.startPolling();
        }
      } catch (error) {
        this.errorResponse = error;
        this.loading = false;
      }
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
  watch: {
    value(newValue) {
      if (!newValue) {
        this.stopPolling();
        this.resetProgressState();
        this.loading = false;
        this.errorsList = null;
        this.successMessage = null;
        this.errorResponse = false;
        this.file = null;
      }
      this.paymentIdentifier = this.findPaymentIdentifier(
        this.school.payment_by_type_id,
      ) || {};
      this.excelFormat = this.school.id ? 'BeeReaders' : 'Innova';
    },
  },
};
</script>
